<i18n>
{
    "uk": {
        "defaultTitle": "Хто викладає",
        "follow": "Читати",
        "following": "Читаєте",
        "toPortfolio": "До портфоліо"
    },
    "en": {
        "defaultTitle": "Our teachers",
        "follow": "Follow",
        "following": "Following",
        "toPortfolio": "To portfolio"
    }
}
</i18n>


<template>
    <div
        v-if="teachers.length > 0 || edit"
        :id="block.id || `${block.type}-${uid}`"
        class="mt-6 mt-md-8 mt-lg-10"
        :class="block.class"
    >
        <div class="page-container">

            <div
                v-if="edit"
                class="edit"
            >
                <button
                    class="btn-bg btn-lg rounded icon-ic-pencil-outline elevate edit-btn"
                    @click="$emit('openTeachersEditor')"
                />
            </div>

            <div class="row">
                <div class="col-12 col-lg-10 offset-lg-1 ta-center">

                    <h2 class="h4 h3-sm h2-md mt-0 mb-2 mb-sm-3 mb-md-4" :class="title.class" v-html="title.text || $t('defaultTitle')" />

                    <div
                        v-if="description.text"
                        class="desc mb-2 mt-sm-n1 mb-sm-3 mt-md-n2 mb-md-4"
                        :class="description.class"
                        v-html="description.text"
                    />

                </div>
            </div>

            <div
                v-if="teachers.length < 3"
                class="row mb-n3 mb-md-n8 mb-lg-0"
            >
                <div
                    v-for="(teacher, i) in teachers"
                    :key="`teacher-${i}`"
                    class="col-12 mb-3 mb-md-8 mb-lg-0"
                    :class="`${teachers.length > 1 ? 'col-lg-6 ' : ''}${content.class}`"
                >

                    <Card
                        class="teacher p-2 p-sm-4"
                        :class="`${teachers.length == 1 ? 'wide ' : ''}${teacher.class}`"
                    >
                        <div class="photo">
                            <WebpPicture
                                class="image"
                                imgClass="album"
                                :src="teacher.photo"
                                :alt="teacher.name"
                                loading="lazy"
                            />
                        </div>

                        <div class="info">

                            <h3 class="name fw-800 h4 h3-sm mt-0 mb-2">{{ teacher.name }}</h3>

                            <div
                                v-if="teacher.position"
                                class="position f-small color-gray-0 mb-2"
                            >
                                {{ teacher.position }}
                            </div>

                            <div
                                v-if="teacher.socials"
                                class="socials mt-1 mb-3"
                            >
                                <div class="d-flex flex-wrap m-nh">

                                    <a
                                        v-for="(social, j) in teacher.socials"
                                        :key="`teacher-${i}-social-${j}`"
                                        :href="$link.protocol(social)"
                                        target="_blank"
                                        class="social btn-secondary fw-600 m-h elevate"
                                    >
                                        <span
                                            class="icon"
                                            :class="icon(social)"
                                        />
                                    </a>

                                </div>
                            </div>

                            <div class="about">
                                <p
                                    v-for="(p, j) in paragraphs(teacher.about)"
                                    :key="`teacher-${i}-p-${j}`"
                                    v-html="p"
                                />
                            </div>

                            <div class="actions">
                                <div class="d-flex flex-wrap m-nh">

                                    <Follow
                                        v-if="teacher.id"
                                        class="btn-secondary fw-600 elevate tt-upper m-h"
                                        modelName="user"
                                        :modelId="teacher.id"
                                    >
                                        <template v-slot:follow>
                                            <span class="icon-ic-user-add-outline fw-normal mr-h" />
                                            {{ $t('follow') }}
                                        </template>

                                        <template v-slot:following>
                                            <span class="icon-ic-tick fw-normal mr-h" />
                                            {{ $t('following') }}
                                        </template>
                                    </Follow>

                                    <a
                                        v-if="teacher.id || teacher.link"
                                        :href="teacher.id && `/${teacher.slug || teacher.id}` || teacher.link"
                                        rel="noopener"
                                        class="btn-secondary fw-600 elevate tt-upper m-h"
                                        target="_blank"
                                    >
                                        {{ $t('toPortfolio') }}
                                        <span class="icon-ic-outer-link ml-h" />
                                    </a>

                                </div>
                            </div>

                        </div>
                    </Card>

                </div>
            </div>

            <div
                v-else
                class="tiles"
            >
                <template
                    v-for="(teacher, i) in teachers"
                >

                    <PanelButton
                        v-if="teacher.about"
                        :key="`teacher-${i}`"
                        class="tile i-link"
                        name="teacher-panel"
                        v-profile="teacher.id ? {
                            modelId:   teacher.id,
                            modelName: 'user',
                            placement: 'bottom-start'
                        } : null"
                        @click.native="currentTeacher = teacher"
                    >
                        <div v-if="teacher.photo" class="photo f-shadow">
                            <WebpPicture
                                class="image"
                                imgClass="userpic"
                                :src="teacher.photo"
                                :alt="teacher.name"
                                loading="lazy"
                            />
                        </div>

                        <h3 class="h5 mt-0 mb-h">{{ teacher.name }}</h3>
                        <div class="f-small color-gray-0">{{ teacher.position }}</div>
                    </PanelButton>

                    <div
                        v-else
                        :key="`teacher-${i}`"
                        class="tile"
                        v-profile="teacher.id ? {
                            modelId:   teacher.id,
                            modelName: 'user',
                            placement: 'bottom-start'
                        } : null"
                    >
                        <div v-if="teacher.photo" class="photo f-shadow">
                            <WebpPicture
                                class="image"
                                imgClass="userpic"
                                :src="teacher.photo"
                                :alt="teacher.name"
                                loading="lazy"
                            />
                        </div>

                        <h3 class="h5 mt-0 mb-h">{{ teacher.name }}</h3>
                        <div class="f-small color-gray-0">{{ teacher.position }}</div>
                    </div>

                </template>

                <Panel
                    ref="motion"
                    modal-name="teacher-panel"
                    side="right"
                    :defTitle="currentTeacher.name"
                    :classes="['panel-teacher h-100 headless wide panel-photo']"
                >
                    <div class="teacher p-2">
                        <div class="photo">
                            <WebpPicture
                                class="image"
                                imgClass="album"
                                :src="currentTeacher.photo"
                                :alt="currentTeacher.name"
                                loading="lazy"
                            />
                        </div>

                        <div class="info">

                            <h3 class="name fw-800 h4 h3-sm mt-0 mb-2">{{ currentTeacher.name }}</h3>

                            <div
                                v-if="currentTeacher.position"
                                class="position f-small color-gray-0 mb-2"
                            >
                                {{ currentTeacher.position }}
                            </div>

                            <div
                                v-if="currentTeacher.socials"
                                class="socials my-3"
                            >
                                <div class="d-flex flex-wrap m-nh">

                                    <a
                                        v-for="(social, i) in currentTeacher.socials"
                                        :key="`teacher-panel-social-${i}`"
                                        :href="$link.protocol(social)"
                                        target="_blank"
                                        class="social btn-secondary m-h"
                                    >
                                        <span
                                            class="icon"
                                            :class="icon(social)"
                                        />
                                    </a>

                                </div>
                            </div>

                            <div
                                v-if="currentTeacher.about"
                                class="about"
                            >
                                <p
                                    v-for="(p, i) in paragraphs(currentTeacher.about)"
                                    :key="`teacher-panel-p-${i}`"
                                    v-html="p"
                                />
                            </div>

                            <div class="actions">
                                <div class="d-flex flex-wrap m-nh">

                                    <Follow
                                        v-if="currentTeacher.id"
                                        class="btn-secondary fw-600 elevate tt-upper m-h"
                                        modelName="user"
                                        :modelId="currentTeacher.id"
                                    >
                                        <template v-slot:follow>
                                            <span class="icon-ic-user-add-outline fw-normal mr-h" />
                                            {{ $t('follow') }}
                                        </template>

                                        <template v-slot:following>
                                            <span class="icon-ic-tick fw-normal mr-h" />
                                            {{ $t('following') }}
                                        </template>
                                    </Follow>

                                    <nuxt-link
                                        v-if="currentTeacher.id"
                                        :to="localePath(`/${currentTeacher.slug || currentTeacher.id}`)"
                                        rel="noopener"
                                        class="btn-secondary fw-600 elevate tt-upper m-h"
                                    >
                                        {{ $t('toPortfolio') }}
                                        <span class="icon-ic-outer-link ml-h" />
                                    </nuxt-link>

                                    <a
                                        v-else-if="currentTeacher.link"
                                        :href="currentTeacher.link"
                                        class="btn-secondary fw-600 elevate tt-upper m-h"
                                        target="_blank"
                                    >
                                        {{ $t('toPortfolio') }}
                                        <span class="icon-ic-outer-link ml-h" />
                                    </a>

                                </div>
                            </div>

                        </div>
                    </div>
                </Panel>
            </div>

        </div>
    </div>
</template>


<script>
    let uid = 0;

    import WebpPicture from '~/components/WebpPicture.vue';

    import blockPropertyObject from '~/mixins/course-landing/blockPropertyObject.js';
    import paragraphs from '~/mixins/course-landing/paragraphs.js';
    import socialIcon from '~/utilities/socialIcon';

    export default {
        props: {
            block: Object,
            edit:  Boolean,
        },

        components: {
            Card:        () => import('~/components/cards/Card.vue'),
            Follow:      () => import('~/components/profile/Follow.vue'),
            Panel:       () => import('~/components/panels/CasesPanel.vue'),
            PanelButton: () => import('~/components/panels/PanelButton.vue'),
            WebpPicture,
        },

        mixins: [
            blockPropertyObject,
            paragraphs,
        ],

        data () {
            uid++;

            return {
                currentTeacher: {},

                uid,
            };
        },

        computed: {
            title () {
                return this.blockPropertyObject(this.block.title, 'text');
            },

            description () {
                return this.blockPropertyObject(this.block.description, 'text');
            },

            content () {
                return this.blockPropertyObject(this.block.content, 'teachers');
            },

            teachers () {
                if (!this.content.teachers) return [];

                let teachers = [];

                this.content.teachers.forEach(chip => {
                    let obj = this.blockPropertyObject(chip, 'name');

                    if (obj) teachers.push(obj);
                });

                return teachers;
            },
        },

        methods: {
            icon (url) {
                return socialIcon(url);
            },

            setCurrentTeacher (teacher) {
                if (!teacher.about) return;

                this.currentTeacher = teacher;
            }
        },
    }
</script>


<style lang="postcss" scoped>
    .desc {
        @media screen and (min-width: 576px) {
            font-size:   19px;
            line-height: 1.263158;
        }

        @media screen and (min-width: 992px) {
            font-size:   24px;
            line-height: 1.166667;
        }
    }

    .position {
        margin-top: -8px;

        @media screen and (min-width: 992px) {
            margin-top: -12px;
        }
    }

    .teacher {
        display:     flex;
        flex-flow:   column nowrap;
        align-items: stretch;
        height:      100%;

        &.wide {
            @media screen and (min-width: 992px) {
                flex-flow: row nowrap;
            }
        }

        .panel-teacher & {
            display: block;

            height:     auto;
            min-height: 100%;
        }
    }

    .photo {
        margin-bottom: 16px;

        .teacher.wide & {
            @media screen and (min-width: 992px) {
                margin-right:  32px;
                margin-bottom: 0;

                flex: 0 0 32%;

                :deep(.album) {
                    height: 260px;
                }
            }

            @media screen and (min-width: 1200px) {
                flex: 0 0 46%;
                flex-basis: 46%;

                :deep(.album) {
                    height: 400px;
                }
            }
        }

        .panel-teacher & {
            margin-right:  0;
            margin-bottom: 16px;
        }
    }

    .info {
        display:     flex;
        flex-flow:   column nowrap;
        align-items: stretch;
        flex:        1 1 auto;

        .panel-teacher & {
            display: block;
        }
    }

    .about {
        flex: 1 0 auto;

        .panel-teacher & {
            flex: 0 0 auto;
        }
    }

    .social {
        font-size: 16px;

        padding: 8px;

        width:  32px;
        height: 32px;
    }

    .tiles {
        margin: -12px -8px;

        display:     flex;
        flex-flow:   row wrap;
        align-items: flex-start;

        @media screen and (min-width: 768px) {
            margin: -16px;
        }

        @media screen and (min-width: 992px) {
            margin: -16px -32px;
        }

        @media screen and (min-width: 1200px) {
            margin: -28px -16px;
        }
    }

    .tile {
        text-align: center;

        padding: 12px 8px;

        flex: 0 0 50%;

        @media screen and (min-width: 768px) {
            text-align: left;

            padding: 16px;

            flex-basis: calc(100% / 3);

            .centered & {
                text-align: center;
            }
        }

        @media screen and (min-width: 992px) {
            padding: 16px 32px;

            flex-basis: 25%;
        }

        @media screen and (min-width: 1200px) {
            padding: 28px 16px;
        }
    }

    .tile .photo {
        margin: 0 auto 8px;
        border-radius: 50%;

        width:    96px;
        height:   96px;
        overflow: hidden;

        @media screen and (min-width: 768px) {
            margin-right: 0;
            margin-left:  0;

            .centered & {
                margin-right: auto;
                margin-left:  auto;
            }
        }
    }

    :deep(.userpic) {
        object-fit:      cover;
        object-position: 50% 0;

        border-radius: 50%;

        display: block;
        width:   96px;
        height:  96px;
    }

    :deep(.album) {
        object-fit:      cover;
        object-position: 50% 0;

        border-radius: 8px;

        aspect-ratio: 1 / 1;
        display:      block;
        width:        100%;

        @media screen and (min-width: 480px) {
            aspect-ratio: auto;
            height:       400px;
        }
    }

    .edit {
        position: relative;
    }

    .edit-btn {
        position: absolute;
        top:      -6px;
        right:    -8px;
        z-index:  10;
    }
</style>
